import React, { ReactElement, useEffect, useState } from 'react'

import { addAlertSignature } from '../../../actions/alerts'
import Employee from '../../../model/employee'
import { EmployeeReducer } from '../../../reducers/employees'
import { visibleComponentDidUpdate } from '../../../utils/component-utils'
import { formatDate, getDate } from '../../../utils/date-utils'
import { formatError } from '../../../utils/error-utils'
import { secureUrl } from '../../../utils/request-utils'
import { t } from '../../../utils/translation-utils'
import Alert from '../../elements/alert'
import Button from '../../elements/button/button'
import Card from '../../elements/card'
import Checkbox from '../../elements/checkbox'
import Col from '../../elements/grid/col'
import Row from '../../elements/grid/row'
import Headline from '../../elements/Headline'
import Input from '../../elements/input/Input'
import Subtitle from '../../elements/Subtitle'
import UserImage from '../../elements/UserImage'
import DeleteEmployeeForm from './DeleteEmployeeForm'

type Props = {
  visible: boolean
  employee?: Employee
  employeeID?: string
  employees: EmployeeReducer
  addAlert: addAlertSignature
  deleteEmployee: (employeeID: string, removeOrphans: boolean) => Promise<boolean | void>
  anonymizeEmployee: (employeeID: string, confirmationText: string) => Promise<boolean | void>
  closeModal: () => void
  cancelModal: () => void
}

export default function DeleteEmployee(props: Props): ReactElement | null {
  const [error, setError] = useState<Error | null>(null)
  const [showPayslipWarning, setShowPayslipWarning] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [nameConfirmation, setNameConfirmation] = useState('')
  const [payslipsConfirmed, setPayslipsConfirmed] = useState(false)
  const [showInputError, setShowInputError] = useState(false)

  const { visible, employees } = props

  useEffect(() => {
    visibleComponentDidUpdate(visible, employees.error, error, setError)
  }, [visible, employees, error])

  useEffect(() => {
    if (visible) {
      setShowPayslipWarning(false)
      setShowConfirmation(false)
      setNameConfirmation('')
      setPayslipsConfirmed(false)
      setShowInputError(false)
    }
  }, [visible])

  let employeeToDelete = props.employee
  if (!employeeToDelete) {
    employeeToDelete = props.employees.employees.find((employee) => employee.id === props.employeeID)
  }

  if (!employeeToDelete) {
    props.closeModal()
    return null
  }

  const employee = employeeToDelete

  // Determine if this is an anonymize case (terminated employee with payroll history)
  const shouldAnonymize = !!employee.immutableEndDate
  const isFreelancer = employee.affiliationType === 'Freelancer'

  const handleInitialSubmit = () => {
    setShowPayslipWarning(true)
  }

  const handlePayslipDownload = () => {
    window.open(secureUrl('/v2/paySlipsZip?employeeID=' + employee.id), '_blank')
  }

  const handleProceedToConfirmation = () => {
    setShowPayslipWarning(false)
    setShowConfirmation(true)
  }

  const handleConfirmedDelete = () => {
    if (!employee.name || nameConfirmation.trim() !== employee.name.trim()) {
      setShowInputError(true)
      return
    }

    // Use anonymize for terminated employees with payroll, delete for draft employees
    const action = shouldAnonymize
      ? props.anonymizeEmployee(employee.id, employee.name)
      : props.deleteEmployee(employee.id, false)

    action.then((success) => {
      if (success) {
        props.addAlert('success', t('employee_delete.alert.success', { name: employee.name || '' }), { timeout: 5 })
        props.closeModal()
        setShowPayslipWarning(false)
        setShowConfirmation(false)
        setNameConfirmation('')
        setPayslipsConfirmed(false)
        setShowInputError(false)
      }
    })
  }

  const handleCancelConfirmation = () => {
    setShowConfirmation(false)
    setNameConfirmation('')
    setShowInputError(false)
  }

  const handleCancelPayslipWarning = () => {
    setShowPayslipWarning(false)
    setPayslipsConfirmed(false)
  }

  const isNameMatch = employee.name ? nameConfirmation.trim() === employee.name.trim() : false

  const getCurrentStep = () => {
    if (showConfirmation) return 3
    if (showPayslipWarning) return 2
    return 1
  }

  const currentStep = getCurrentStep()

  // Get appropriate intro text based on employee type and state
  const getIntroLine2 = () => {
    if (isFreelancer) {
      return shouldAnonymize
        ? t('employee_delete.intro.freelancer.line_2.anonymize')
        : t('employee_delete.intro.freelancer.line_2')
    }
    return shouldAnonymize
      ? t('employee_delete.intro.employee.line_2.anonymize')
      : t('employee_delete.intro.employee.line_2')
  }

  return (
    <Card className="employees-terminate">
      <Subtitle>{isFreelancer ? t('employee_delete.title.freelancer') : t('employee_delete.title.employee')}</Subtitle>

      <div className="employees-terminate-steps">
        <span className={currentStep === 1 ? 'active' : currentStep > 1 ? 'completed' : ''}>
          {t('employee_delete.steps.review')}
        </span>
        <span className="employees-terminate-step-arrow">→</span>
        <span className={currentStep === 2 ? 'active' : currentStep > 2 ? 'completed' : ''}>
          {t('employee_delete.steps.download')}
        </span>
        <span className="employees-terminate-step-arrow">→</span>
        <span className={currentStep === 3 ? 'active' : ''}>{t('employee_delete.steps.confirm')}</span>
      </div>

      <p>
        {isFreelancer ? t('employee_delete.intro.freelancer.line_1') : t('employee_delete.intro.employee.line_1')}
        <br />
        {getIntroLine2()}
      </p>

      {error && <Alert message={formatError(error)} type="error" showIcon />}

      <Row>
        <Col span={12} offset={6}>
          <Card className="employees-terminate-profile">
            <UserImage name={employee.name || ''} size="small" />
            <div className="employees-terminate-profile-data">
              <Headline>{employee.name || ''}</Headline>
              {(employee.activeEmployment || employee.earliestMutableContract) && (
                <p>
                  {formatDate(
                    employee.activeEmployment
                      ? employee.activeEmployment.startDate
                      : employee.earliestMutableContract
                      ? employee.earliestMutableContract.validFrom
                      : getDate()
                  )}
                </p>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {!showPayslipWarning && !showConfirmation ? (
        <DeleteEmployeeForm
          employee={employee}
          employees={props.employees}
          onSubmit={handleInitialSubmit}
          onCancel={props.cancelModal}
        />
      ) : showPayslipWarning ? (
        <div className="employees-terminate-payslip-warning">
          <Alert message={t('employee_delete.payslip_warning.title')} type="warning" showIcon />
          <p className="employees-terminate-payslip-warning-description">
            {t('employee_delete.payslip_warning.description')}
          </p>

          <div className="employees-terminate-payslip-warning-content">
            <p>{t('employee_delete.payslip_warning.legal_requirement')}</p>

            <Button type="primary" prefixIcon="arrowDownIntoTray" size="extra-large" onClick={handlePayslipDownload}>
              {t('employee_delete.payslip_warning.download_button')}
            </Button>

            <div className="employees-terminate-payslip-checkbox">
              <Checkbox checked={payslipsConfirmed} onChange={(e) => setPayslipsConfirmed(e.target.checked)}>
                {t('employee_delete.payslip_warning.confirmation_checkbox')}
              </Checkbox>
            </div>
          </div>

          <div className="employees-terminate-confirmation-buttons">
            <Button type="secondary" onClick={handleCancelPayslipWarning} size="extra-large">
              {t('common.cancel')}
            </Button>
            <Button
              type="primary"
              size="extra-large"
              onClick={handleProceedToConfirmation}
              disabled={!payslipsConfirmed}
            >
              {t('employee_delete.payslip_warning.continue_button')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="employees-terminate-confirmation">
          <Alert
            message={t('employee_delete.confirmation.message', { name: employee.name || '' })}
            type="warning"
            showIcon
          />
          <p className="employees-terminate-confirmation-final-warning">
            {t('employee_delete.confirmation.final_warning')}
          </p>
          <p className="employees-terminate-confirmation-instructions">
            {t('employee_delete.confirmation.instructions')}
          </p>

          <Input
            type="text"
            value={nameConfirmation}
            onChange={(e) => {
              setNameConfirmation(e.target.value)
              setShowInputError(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleConfirmedDelete()
              }
            }}
            autoFocus
            size="large"
            className={`employees-terminate-confirmation-input ${showInputError ? 'input-error' : ''}`}
          />

          <div className="employees-terminate-confirmation-buttons">
            <Button type="secondary" onClick={handleCancelConfirmation} size="extra-large">
              {t('common.cancel')}
            </Button>
            <Button
              type="primary"
              size="extra-large"
              onClick={handleConfirmedDelete}
              className={!isNameMatch ? 'disabled-style' : ''}
              danger
            >
              {t('employee_delete.button.confirm_delete')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}
